import React, { useEffect, useState, useRef } from 'react';
import Footer from '../components/Footer';
import '../styles/Features.css';

const Features = () => {
    const [dvdPosition, setDvdPosition] = useState({ x: 0, y: 0 });
    const containerRef = useRef(null);
    const animationFrameRef = useRef(null);
    const velocityRef = useRef({ dx: 2, dy: 1.5 });
    const positionRef = useRef({ x: 0, y: 0 });

    useEffect(() => {
        // Add class to body when component mounts
        document.body.classList.add('features-page-active');

        // Remove class when component unmounts
        return () => {
            document.body.classList.remove('features-page-active');
        };
    }, []);

    useEffect(() => {
        // Get velocity based on screen size (slower on mobile)
        const getVelocity = () => {
            if (window.innerWidth < 480) {
                return { dx: 0.6, dy: 0.45 };
            }
            return { dx: 2, dy: 1.5 };
        };

        // Initialize velocity based on screen size
        velocityRef.current = getVelocity();

        // Get actual SVG dimensions from computed styles (handles responsive sizing)
        const getDvdDimensions = () => {
            const svg = containerRef.current?.querySelector('svg');
            if (svg) {
                const computedStyle = window.getComputedStyle(svg);
                return {
                    width: parseFloat(computedStyle.width) || 120,
                    height: parseFloat(computedStyle.height) || 75
                };
            }
            return { width: 120, height: 75 };
        };

        // Initialize DVD position randomly within container bounds
        const initializePosition = () => {
            if (containerRef.current) {
                const container = containerRef.current;
                const containerRect = container.getBoundingClientRect();
                const { width: dvdWidth, height: dvdHeight } = getDvdDimensions();
                const maxWidth = containerRect.width - dvdWidth;
                const maxHeight = containerRect.height - dvdHeight;
                
                if (maxWidth > 0 && maxHeight > 0) {
                    const initX = Math.random() * maxWidth;
                    const initY = Math.random() * maxHeight;
                    
                    positionRef.current = { x: initX, y: initY };
                    setDvdPosition({ x: initX, y: initY });
                    return true;
                }
            }
            return false;
        };

        const animate = () => {
            if (containerRef.current) {
                const container = containerRef.current;
                const containerRect = container.getBoundingClientRect();
                const { width: dvdWidth, height: dvdHeight } = getDvdDimensions();
                const maxWidth = containerRect.width - dvdWidth;
                const maxHeight = containerRect.height - dvdHeight;
                
                if (maxWidth > 0 && maxHeight > 0) {
                    let { x, y } = positionRef.current;
                    let { dx, dy } = velocityRef.current;

                    // Update position
                    x += dx;
                    y += dy;

                    // Check boundaries and reverse velocity
                    if (x <= 0 || x >= maxWidth) {
                        dx = -dx;
                        x = Math.max(0, Math.min(x, maxWidth));
                    }
                    if (y <= 0 || y >= maxHeight) {
                        dy = -dy;
                        y = Math.max(0, Math.min(y, maxHeight));
                    }

                    // Clamp position to boundaries
                    x = Math.max(0, Math.min(x, maxWidth));
                    y = Math.max(0, Math.min(y, maxHeight));

                    positionRef.current = { x, y };
                    velocityRef.current = { dx, dy };
                    setDvdPosition({ x, y });
                }
            }

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        // Wait for container to be ready, then initialize and start animation
        const startTimer = setTimeout(() => {
            if (initializePosition()) {
                animationFrameRef.current = requestAnimationFrame(animate);
            }
        }, 300);

        // Handle window resize
        const handleResize = () => {
            // Update velocity based on new screen size
            velocityRef.current = getVelocity();
            
            if (containerRef.current) {
                const container = containerRef.current;
                const containerRect = container.getBoundingClientRect();
                const { width: dvdWidth, height: dvdHeight } = getDvdDimensions();
                const maxWidth = containerRect.width - dvdWidth;
                const maxHeight = containerRect.height - dvdHeight;
                
                // Clamp current position to new boundaries
                positionRef.current.x = Math.max(0, Math.min(positionRef.current.x, maxWidth));
                positionRef.current.y = Math.max(0, Math.min(positionRef.current.y, maxHeight));
                setDvdPosition(positionRef.current);
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            clearTimeout(startTimer);
            window.removeEventListener('resize', handleResize);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    return (
        <div className="features-page">
            <div className="features-content">
                <div className="dust-container">
                    <div className="dust-particle dust-1"></div>
                    <div className="dust-particle dust-2"></div>
                    <div className="dust-particle dust-3"></div>
                    <div className="dust-particle dust-4"></div>
                    <div className="dust-particle dust-5"></div>
                    <div className="dust-particle dust-6"></div>
                    <div className="dust-particle dust-7"></div>
                    <div className="dust-particle dust-8"></div>
                    <div className="dust-particle dust-9"></div>
                    <div className="dust-particle dust-10"></div>
                    <div className="dust-particle dust-11"></div>
                    <div className="dust-particle dust-12"></div>
                    <div className="dust-particle dust-13"></div>
                    <div className="dust-particle dust-14"></div>
                    <div className="dust-particle dust-15"></div>
                    <div className="dust-particle dust-16"></div>
                    <div className="dust-particle dust-17"></div>
                    <div className="dust-particle dust-18"></div>
                    <div className="dust-particle dust-19"></div>
                    <div className="dust-particle dust-20"></div>
                    <div className="dust-particle dust-21"></div>
                    <div className="dust-particle dust-22"></div>
                    <div className="dust-particle dust-23"></div>
                    <div className="dust-particle dust-24"></div>
                </div>
                <div className="vintage-image-container">
                    <img src="/images/vintage.png" alt="Vintage" className="vintage-image" />
                    <div className="dvd-container" ref={containerRef}>
                        <div 
                            className="dvd-bounce"
                            style={{
                                left: `${dvdPosition.x}px`,
                                top: `${dvdPosition.y}px`
                            }}
                        >
                            <svg width="120" height="75" viewBox="0 0 120 75" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M60 57.8751C93.1371 57.8751 120 61.6725 120 66.3568C120 71.0411 93.1372 74.8385 60 74.8385C26.8629 74.8385 0 71.0411 0 66.3568C0.000103627 61.6725 26.863 57.8751 60 57.8751ZM57.8012 63.5293C50.1676 63.5293 43.9796 64.8658 43.9796 66.514C43.9798 68.1621 50.1677 69.4978 57.8012 69.4978C65.4347 69.4978 71.6233 68.1621 71.6236 66.514C71.6236 64.8658 65.4349 63.5293 57.8012 63.5293Z" fill="#040404"/>
                                <path d="M33.9316 0C48.4668 0 60.2499 11.7831 60.2499 26.3183C60.2499 40.8534 48.4668 52.6365 33.9316 52.6365C19.3965 52.6365 7.61339 40.8534 7.61336 26.3183C7.61336 11.7831 19.3965 1.77858e-05 33.9316 0Z" fill="#040404"/>
                                <path d="M60.2499 0C63.706 4.18194e-06 67.1284 0.680553 70.3214 2.00314C73.5145 3.32576 76.4159 5.26458 78.8597 7.70845C81.3036 10.1523 83.2424 13.0537 84.565 16.2468C85.8876 19.4398 86.5682 22.8622 86.5682 26.3183C86.5682 29.7744 85.8876 33.1968 84.565 36.3898C83.2424 39.5829 81.3036 42.4842 78.8597 44.9281C76.4159 47.372 73.5145 49.3108 70.3214 50.6334C67.1284 51.956 63.706 52.6365 60.2499 52.6365V0Z" fill="#040404"/>
                                <path d="M86.5682 0C90.0243 1.30499e-05 93.4467 0.680546 96.6397 2.00314C99.8328 3.32576 102.734 5.26458 105.178 7.70845C107.622 10.1523 109.561 13.0537 110.883 16.2468C112.206 19.4398 112.886 22.8622 112.886 26.3183C112.886 29.7744 112.206 33.1968 110.883 36.3898C109.561 39.5828 107.622 42.4842 105.178 44.9281C102.734 47.372 99.8328 49.3108 96.6397 50.6334C93.4467 51.956 90.0243 52.6365 86.5682 52.6365V0Z" fill="#040404"/>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
            <div className="features-text-container">
                <div className="features-text-title mb-16">
                    <p>
                        <b>All features</b>
                    </p>
                </div>
                <div className="features-group">
                    <div className="features-group-left">
                        <div className="features-group-left-sticky">
                            General
                        </div>
                    </div>
                    <div className="features-group-right">
                        <div className="features-text">
                            <p>
                                <b>Commands. </b>
                                Use contextual commands to quickly add scenes, sequences, characters, locations, spellcheck, find and replace, and much much more, using ⌘K or Ctrl+K.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Search. </b>
                                Search for- and jump to any page in the app with ⌘K or Ctrl+K.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Dark mode. </b>
                                Prefer working in the dark? No problem.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Preferred page. </b>
                                Select a preferred page that you want the app to open to by default.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Focus. </b>
                                Pin characters, locations, and specific points in your script to your sidebar to quickly access them later.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Keyboard shortcuts. </b>
                                Fiktion is packed with keyboard shortcuts to help you work faster and more efficiently.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Scripts from Script Slug. </b>
                                Find direct links to hundreds of scripts, right inside the app. 
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Quote library. </b>
                                Discover hundreds of legendary quotes, right inside the app.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Multiple projects. </b>
                                Create multiple projects for your different scripts and ideas, all in a single workspace. 
                            </p>
                        </div>
                    </div>
                </div>
                <div className="features-group">
                    <div className="features-group-left">
                        <div className="features-group-left-sticky">
                            Outline
                        </div>
                    </div>
                    <div className="features-group-right">
                        <div className="features-text">
                            <p>
                                <b>Outline themes. </b>
                                Pick a preferred outline theme to use when outlining your story.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Canvas view. </b>
                                Outline your story using the canvas view. A large free-flowing whiteboard.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Kanban view. </b>
                                Outline your story using the Kanban view. Vertical columns with drag and drop functionality.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>List view. </b>
                                Outline your story using the List view. A vertical compressed view to view your story from top to bottom.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Scenes. </b>
                                Create scenes with titles and descriptions.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Sequences. </b>
                                Create sequences to group your scenes together.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Page goals. </b>
                                Set page goals for each scene to help you estimate your script's length.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Timeline. </b>
                                See your story unfold across acts in a timeline. The timeline can be summoned directly in the script editor to help you stay true to your outline as you write.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Story map. </b>
                                A high-level overview of your script. Jump to every scene with just one click.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Styles. </b>
                                Style scenes and sequences on the canvas, with borders or full color, individually or all together depending on their importance. 
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Unassign. </b>
                                Unassign scenes or sequences, and use it as a way to temporarily archive them. 
                            </p>
                        </div>
                    </div>
                </div>
                <div className="features-group">
                    <div className="features-group-left">
                        <div className="features-group-left-sticky">
                            Script
                        </div>
                    </div>
                    <div className="features-group-right">
                        <div className="features-text">
                            <p>
                                <b>Go to. </b>
                                Search for- and jump to any scene in your script.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Spellcheck. </b>
                                Run a spellcheck on your script to catch any spelling errors.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Script notes. </b>
                                Think on the page, track ideas, and revise with clarity while keeping the script clean.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Find and replace. </b>
                                Quickly search and replace text across your script.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Script report. </b>
                                Get a report on your script's structure, characters, diversity, race representation, and more.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Timeline. </b>
                                Summon the story's timeline directly in the script editor to help you stay true to your outline as you write.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Story map. </b>
                                A high-level overview of your script. Jump to every scene with just one click.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Version history. </b>
                                Track every change you make to your script, with the ability to revert to any previous version.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Auto save. </b>
                                Your script is automatically saved on every 500th character and every 10th minuteto prevent loss of work.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Manual save. </b>
                                Save your script manually and give that version a specific name.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Zoom. </b>
                                Zoom in and out of your script.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Hide UI. </b>
                                Hide the UI to focus on your script.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Hide Sidebar. </b>
                                Hide the sidebar, but keep the script features visible.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Fullscreen. </b>
                                Fullscreen your script to see it in all its glory.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Industry standard formatting. </b>
                                Automatically formatted for you.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>TAB. </b>
                                Use the TAB key to cycle through elements.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Suggestions. </b>
                                The editor will suggest characters, locations, time of day, transitions, and more as you type.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Save character and locations. </b>
                                Save character and location names right from the editor and write them out in their respective pages.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Export PDF. </b>
                                Export your script as a PDF, and automatically include page breaks, (MORE) and (CONT'D).
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Export XML. </b>
                                Export your file as an XML file for local backup.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Title page. </b>
                                Access the title page with the flick of a switch.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Upload XML file. </b>
                                Upload an XML file to import your script.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Upload FDX file. </b>
                                Upload an FDX file to import your script from Final Draft.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="features-group">
                    <div className="features-group-left">
                        <div className="features-group-left-sticky">
                            Ideas
                        </div>
                    </div>
                    <div className="features-group-right">
                        <div className="features-text">
                            <p>
                                <b>Notepad. </b>
                                A familiar notepad for jotting down ideas, thoughts, and inspiration.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Tabs. </b>
                                Organize your notes into tabs like 'ideas', 'research', 'inspiration', etc., and switch between them with a click.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Link embeds. </b>
                                Embed links for a more visual experience.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Youtube embeds. </b>
                                Embed Youtube videos if you find guidance or inspiration.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="features-group">
                    <div className="features-group-left">
                        <div className="features-group-left-sticky">
                            Project
                        </div>
                    </div>
                    <div className="features-group-right">
                        <div className="features-text">
                            <p>
                                <b>Icon & Color. </b>
                                Pick an icon and color that matches the genre of your script.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Logline. </b>
                                Add a logline to your script.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Plot summary. </b>
                                Add a plot summary to your script.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Genres. </b>
                                Pick the genres that best describe your script.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Outline structure. </b>
                                Select if you want to outline using Act 1-3 or Act 1, Act 2A-B, Act 3.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Target dates. </b>
                                Add a start date a deadline, and follow your progress as you move along.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="features-group">
                    <div className="features-group-left">
                        <div className="features-group-left-sticky">
                            Characters
                        </div>
                    </div>
                    <div className="features-group-right">
                        <div className="features-text">
                            <p>
                                <b>Create characters. </b>
                                With names, descriptions, goals, obstacles, and more.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Character traits. </b>
                                Add their age, gender, ethnicity, occupation and more.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Type of character. </b>
                                Organize your characters by Lead, Supporting or Minor.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="features-group">
                    <div className="features-group-left">
                        <div className="features-group-left-sticky">
                            Locations
                        </div>
                    </div>
                    <div className="features-group-right">
                        <div className="features-text">
                            <p>
                                <b>Create Locations. </b>
                                With names, descriptions and details.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Character traits. </b>
                                Add their age, gender, ethnicity, occupation and more.
                            </p>
                        </div>
                        <div className="features-text">
                            <p>
                                <b>Type of location. </b>
                                Organize your locations by Primary, Secondary or One-off locations.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default Features;

